import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchStrategies } from '../api';

const REFRESH_INTERVAL = 10000;

const TYPE_META = {
  atm_straddle:     { label: 'ATM Straddle',     icon: '⚡', accent: '#58a6ff' },
  dynamic_atm_straddle: { label: 'Dynamic ATM Straddle', icon: '🧠', accent: '#7ee787' },
  opening_range:    { label: 'Opening Range',     icon: '📐', accent: '#f0883e' },
  manual_single_leg:{ label: 'Manual Trade',      icon: '🎯', accent: '#a371f7' },
  rolling_strangle: { label: 'Rolling Strangle',  icon: '🔄', accent: '#3fb950' },
  ladder_straddle:  { label: 'Ladder Straddle',   icon: '🪜', accent: '#d29922' },
  indicator_aligned:{ label: 'Indicator Aligned', icon: '🎚️', accent: '#56d364' },
  major_stocks_aligned:{ label: 'Major Stocks Aligned', icon: '📊', accent: '#39c5cf' },
  hedge_only:       { label: 'Hedge Only',        icon: '🛡️', accent: '#bc8cff' },
};

const TAB_FILTERS = [
  { key: '',                   label: 'All' },
  { key: 'straddle',           label: 'Straddles' },
  { key: 'rolling_strangle',   label: 'Strangle' },
];

const DAY_NAMES = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const TODAY = DAY_NAMES[new Date().getDay()];

export default function Strategies() {
  const [stratData, setStratData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [filterIndex, setFilterIndex] = useState('');
  const [filterDay, setFilterDay] = useState(TODAY);
  const [filterMode, setFilterMode] = useState('live');
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const res = await fetchStrategies();
      if (res.data?.strategies) setStratData(res.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading && !stratData) {
    return <div className="loading"><div className="spinner" /><p>Loading strategies...</p></div>;
  }

  const allStrats = stratData?.strategies || [];

  // Apply tab filter
  const tabFiltered = allStrats.filter(s => {
    if (!activeTab) return true;
    if (activeTab === 'straddle') return s.entry_type === 'atm_straddle' || s.entry_type === 'dynamic_atm_straddle' || s.entry_type === 'opening_range';
    return s.entry_type === activeTab;
  });

  // Apply secondary filters
  const strategies = tabFiltered.filter(s =>
    (!filterIndex || s.index === filterIndex) &&
    (!filterDay || s.trading_day === filterDay || !s.trading_day) &&
    (!filterMode || (filterMode === 'paper' ? s.paper_trading : !s.paper_trading))
  );

  const totalPnl = strategies.reduce((sum, s) => sum + (s.pnl_amount || 0), 0);
  const activeCount = strategies.filter(s => s.state === 'ACTIVE').length;
  const liveCount = strategies.filter(s => !s.paper_trading).length;

  // Tab counts
  const tabCounts = {
    '': allStrats.length,
    straddle: allStrats.filter(s => s.entry_type === 'atm_straddle' || s.entry_type === 'dynamic_atm_straddle' || s.entry_type === 'opening_range').length,
    rolling_strangle: allStrats.filter(s => s.entry_type === 'rolling_strangle').length,
  };

  const formatAmount = (v) => {
    const abs = Math.abs(v);
    if (abs >= 100000) return '\u20B9' + (abs / 100000).toFixed(2) + 'L';
    return '\u20B9' + abs.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const getCardSubtext = (strat) => {
    if (strat.entry_type === 'manual_single_leg') return 'Single-leg manual orders';
    if (strat.entry_type === 'rolling_strangle') {
      const phase = strat.params?.phase || 'IDLE';
      return `${phase} phase \u00b7 ${strat.params?.reform_count || 0} reforms`;
    }
    return `${strat.adjustment_points}pt adj \u00b7 ${strat.strike_interval}pt interval`;
  };

  const handleCardClick = (strat) => {
    if (strat.entry_type === 'manual_single_leg') navigate('/quick-order');
    else if (strat.entry_type === 'rolling_strangle') navigate('/rolling-strangle');
    else navigate(`/strategies/${strat.name}`);
  };

  const meta = (s) => TYPE_META[s.entry_type] || TYPE_META.atm_straddle;

  const renderCard = (strat) => {
    const pnlAmt = strat.pnl_amount || 0;
    const isActive = strat.state === 'ACTIVE';
    const m = meta(strat);
    const posCount = strat.positions?.length || 0;
    return (
      <div
        key={strat.name}
        className={`sl-card ${isActive ? 'sl-card-active' : ''}`}
        style={{ borderLeftColor: isActive ? m.accent : undefined }}
        onClick={() => handleCardClick(strat)}
      >
        {/* Type accent strip */}
        <div className="sl-card-accent" style={{ background: m.accent }} />

        <div className="sl-card-top">
          <div className="sl-card-type-badge" style={{ background: m.accent + '18', color: m.accent }}>
            <span>{m.icon}</span> {m.label}
          </div>
          <span className={`strat-state-badge strat-state-${strat.state?.toLowerCase()}`}>{strat.state}</span>
        </div>

        <div className="sl-card-body">
          <div className="sl-card-identity">
            <h3 className="sl-card-name">
              {strat.index}{strat.trading_day ? ` \u00b7 ${strat.trading_day}` : ''}
            </h3>
            <p className="sl-card-sub">{getCardSubtext(strat)}</p>
          </div>
          <div className={`sl-card-pnl-block ${pnlAmt >= 0 ? 'positive' : 'negative'}`}>
            <span className="sl-card-pnl-value">{pnlAmt >= 0 ? '+' : '-'}{formatAmount(pnlAmt)}</span>
            <span className="sl-card-pnl-label">P&L</span>
          </div>
        </div>

        <div className="sl-card-info-row">
          <div className="sl-card-info-item">
            <span className="sl-info-label">Account</span>
            <span className="sl-info-value">{strat.account_name || 'Paper'}</span>
          </div>
          <div className="sl-card-info-item">
            <span className="sl-info-label">Lots</span>
            <span className="sl-info-value">{strat.lots || 1}</span>
          </div>
          <div className="sl-card-info-item">
            <span className="sl-info-label">Window</span>
            <span className="sl-info-value">{strat.start_time} – {strat.exit_time}</span>
          </div>
          <div className="sl-card-info-item">
            <span className="sl-info-label">Positions</span>
            <span className={`sl-info-value ${posCount > 0 ? 'sl-info-highlight' : ''}`}>{posCount}</span>
          </div>
        </div>

        <div className="sl-card-bottom">
          <span className={`sl-mode-tag ${strat.paper_trading ? 'sl-mode-paper' : 'sl-mode-live'}`}>
            {strat.paper_trading ? 'Paper' : 'Live'}
          </span>
          {strat.last_action && (
            <span className="sl-last-evt">
              {strat.last_action}
              {strat.last_action_time && <span className="sl-last-time"> · {strat.last_action_time}</span>}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-strategies">
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Strategies</h1>
          <p className="page-subtitle">Monitor and manage all trading strategies</p>
        </div>
      </div>

      {/* Stats Bar */}
      <section className="sl-stats-bar">
        <div className="sl-stat-item">
          <div className={`sl-stat-mode ${liveCount > 0 ? 'live' : ''}`}>
            <span className={`mode-dot ${liveCount > 0 ? 'live-mode' : 'paper'}`} />
            {liveCount > 0 ? `${liveCount} LIVE` : 'ALL PAPER'}
          </div>
        </div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Expiry</span>
          <span className="sl-stat-value">{stratData?.expiry || '—'}</span>
        </div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Active</span>
          <span className="sl-stat-value sl-active-val">{activeCount} / {strategies.length}</span>
        </div>
        <div className="sl-stat-item">
          <span className="sl-stat-label">Total P&L</span>
          <span className={`sl-stat-value ${totalPnl >= 0 ? 'positive' : 'negative'}`}>
            {totalPnl >= 0 ? '+' : '-'}₹{Math.abs(totalPnl).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </span>
        </div>
      </section>

      {/* Type Tabs + Filters */}
      <div className="sl-toolbar">
        <div className="sl-type-tabs">
          {TAB_FILTERS.map(t => (
            <button
              key={t.key}
              className={`sl-type-tab ${activeTab === t.key ? 'sl-type-tab-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
              {tabCounts[t.key] > 0 && <span className="sl-tab-count">{tabCounts[t.key]}</span>}
            </button>
          ))}
        </div>
        <div className="sl-secondary-filters">
          <select value={filterDay} onChange={e => setFilterDay(e.target.value)}>
            <option value="">All Days</option>
            {['Monday','Tuesday','Wednesday','Thursday','Friday'].map(d => (
              <option key={d} value={d}>{d}{d === TODAY ? ' (Today)' : ''}</option>
            ))}
          </select>
          <select value={filterIndex} onChange={e => setFilterIndex(e.target.value)}>
            <option value="">All Indices</option>
            <option value="Nifty">Nifty</option>
            <option value="BankNifty">BankNifty</option>
            <option value="Sensex">Sensex</option>
          </select>
          <select value={filterMode} onChange={e => setFilterMode(e.target.value)}>
            <option value="">All Modes</option>
            <option value="paper">Paper</option>
            <option value="live">Live</option>
          </select>
        </div>
      </div>

      {/* Strategy Cards Grid */}
      {strategies.length > 0 ? (
        <div className="sl-card-grid">
          {strategies.map(renderCard)}
        </div>
      ) : (
        <div className="dash-empty">
          {allStrats.length === 0
            ? <>No strategies configured yet. Go to <button className="dash-link" onClick={() => navigate('/settings')}>Settings</button> to add strategy instances.</>
            : 'No strategies match the current filters.'}
        </div>
      )}
    </div>
  );
}
